import {
  applyEdgeChanges,
  applyNodeChanges,
  BuiltInEdge,
  Edge,
  EdgeChange,
  Node,
  NodeChange,
  useReactFlow,
} from "@xyflow/react"
import "@xyflow/react/dist/style.css"
import { Form } from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { DefaultValues, UseFormReturn } from "react-hook-form"
import { WorkflowImportPreviewResponseDataSchema } from "schema"
import { z } from "zod"
import "./flowStyles.css"
import { BuilderSaveContext } from "./hooks/useBuilderSave"
import { CARD_HEIGHT, CARD_SPACING, CARD_WIDTH } from "./lib/constants"
import { getInitialNodesAndEdges } from "./lib/getInitialNodesAndEdges"
import { layoutNodes } from "./lib/repositionNodes"
import { BuilderFormValues, computeGroupBoxes, GROUP_BANNER_HEIGHT } from "./lib/workflowsCanvas"

// The campaign or settings scope the builder edits. Just an id + display name.
type BuilderContainer = { id: string; name: string | null }

type ImportPreview = z.infer<typeof WorkflowImportPreviewResponseDataSchema>["blueprint"]

// Group box + banner + toolbar nodes are derived; everything else is a content node
// (event/condition/action).
const isContentNode = (node: Node) =>
  node.type !== "workflowGroup" && node.type !== "workflowBanner" && node.type !== "workflowToolbar"

type Props = {
  children: React.ReactNode
  container: BuilderContainer
  form: UseFormReturn<BuilderFormValues>
  flush: () => Promise<void>
  sync: (defaults: DefaultValues<BuilderFormValues>) => void
  defaults: DefaultValues<BuilderFormValues>
}

type BuilderContextType = {
  container: BuilderContainer
  form: UseFormReturn<BuilderFormValues>
  nodes: Node[]
  edges: Edge[]
  setNodes: (nodes: NodeChange[]) => void
  setEdges: (edges: EdgeChange[]) => void
  focusedNode: Node | null
  setFocusedNode: (id: string | null) => void
  onAddWorkflow: () => void
  onImportWorkflow: (preview: ImportPreview) => void
  onAddEventNode: (workflowId: string) => void
  onToggleWorkflowEnabled: (workflowId: string) => void
  onDeleteWorkflow: (workflowId: string) => void
  onAddConditionNode: (
    nodeId: string,
    kind: "event" | "condition" | "action",
    branch?: "Pass" | "Fail",
  ) => void
  onAddActionNode: (
    nodeId: string,
    kind: "event" | "condition" | "action",
    branch?: "Pass" | "Fail",
  ) => void
  onDeleteNode: (nodeId: string) => void
}

// eslint-disable-next-line react-refresh/only-export-components
export const BuilderContext = createContext<BuilderContextType | undefined>(undefined)

export function BuilderProvider({ children, container, form, flush, sync, defaults }: Props) {
  const { setCenter, getZoom } = useReactFlow()

  useEffect(() => {
    sync(defaults)
  }, [defaults, sync])

  const saveCtx = useMemo(() => ({ flush }), [flush])

  // NOTE: This ref hack makes sure we only call this once after the form is initialized
  const {
    nodes: initialComputedNodes,
    edges: initialComputedEdges,
    commonEdgeOptions,
  } = useRef(getInitialNodesAndEdges({ form })).current

  // Prepend the per-workflow group boxes so they live in React Flow's controlled node state (and
  // thus get measured + pointer events, exactly like the cards). Content nodes must be passed
  // through `layoutNodes` first.
  const withBoxes = useCallback(
    (laidContent: Node[]) => {
      const workflows = form.getValues("workflows") ?? []
      // Push a disabled workflow's cards down to make room for its banner row. layoutNodes derives Y
      // from depth, so this offset is recomputed from canonical positions every pass — never
      // compounds. computeGroupBoxes cancels it on the box-top so all workflows stay top-aligned.
      const disabledIds = new Set(workflows.filter((w) => !w.isEnabled).map((w) => w.id))
      const content = laidContent.map((node) => {
        const workflowId = (node.data as { workflowId?: string } | undefined)?.workflowId
        if (!workflowId || !disabledIds.has(workflowId)) return node
        return { ...node, position: { ...node.position, y: node.position.y + GROUP_BANNER_HEIGHT } }
      })
      return [...computeGroupBoxes(content, workflows), ...content]
    },
    [form],
  )

  const [nodes, setNodes] = useState<Node[]>(() => withBoxes(initialComputedNodes))
  const [edges, setEdges] = useState<Edge[]>(() => initialComputedEdges)

  const [focusedNode, setFocusedNode] = useState<Node | null>(null)

  const createNode = useCallback(
    (
      level: number,
      type: "event" | "condition" | "action",
      sourceNodeId: string,
      branch: "Pass" | "Fail",
    ) => {
      const parentId = sourceNodeId
      // Children inherit the workflow group of their parent.
      const workflowId = form.getValues("nodes")?.find((n) => n.id === sourceNodeId)?.workflowId

      const newNodeId = crypto.randomUUID()

      const newNode: Node = {
        id: newNodeId,
        type,
        data: { workflowId },
        draggable: false,
        // Seed position is irrelevant — layoutNodes derives every coordinate from the tree.
        position: { x: 0, y: level },
      }

      const newEdge: BuiltInEdge = {
        id: `edge-${sourceNodeId}-${newNodeId}`,
        source: sourceNodeId,
        sourceHandle:
          form.getValues("nodes")?.find((n) => n.id === sourceNodeId)?.type === "Condition"
            ? branch
            : "source",
        target: newNodeId,
        ...commonEdgeOptions,
      }

      const numberOfNodes = form.getValues("nodes")?.length ?? 0

      switch (type) {
        case "event":
          form.setValue(
            `nodes.${numberOfNodes}`,
            {
              id: newNodeId,
              parentId,
              branch,
              type: "Event",
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              event: undefined,
            },
            {
              shouldDirty: true,
            },
          )
          break

        case "condition":
          form.setValue(
            `nodes.${numberOfNodes}`,
            {
              id: newNodeId,
              parentId,
              branch,
              type: "Condition",
              workflowId,
              conditions: [
                {
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-expect-error
                  field: undefined,
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-expect-error
                  operator: undefined,
                  value: undefined,
                  config: {},
                },
              ],
              config: {},
            },
            {
              shouldDirty: true,
            },
          )
          break

        case "action":
          form.setValue(
            `nodes.${numberOfNodes}`,
            {
              id: newNodeId,
              parentId,
              branch,
              type: "Action",
              workflowId,
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              action: undefined,
              config: {},
            },
            {
              shouldDirty: true,
            },
          )
          break
      }

      return { newNode, newEdge }
    },
    [commonEdgeOptions, form],
  )

  const centerNode = useCallback(
    (node: Node) => {
      // Sheet sits on the left (~480px wide). Place the card center in the
      // middle of the remaining (right) free area so it stays fully visible.
      const SHEET_WIDTH = 480
      setCenter(
        node.position.x + CARD_WIDTH / 2 - SHEET_WIDTH / 2,
        node.position.y + CARD_HEIGHT / 2,
        {
          duration: 500,
          zoom: getZoom(),
        },
      )
    },
    [getZoom, setCenter],
  )

  const setFocusedNodeId = useCallback(
    (id: string | null) => {
      if (!id) {
        setFocusedNode(null)
        return
      }

      const node = nodes.find((n) => n.id === id)
      if (!node) return

      centerNode(node)
      setFocusedNode(node)
    },
    [nodes, centerNode, setFocusedNode],
  )

  const addNodeAndUpdateFlow = useCallback(
    (params: {
      sourceNodeId: string
      nodeType: "event" | "condition" | "action"
      level: number
      sourceNodeType?: "event" | "condition" | "action" | "channel"
      branch?: "Pass" | "Fail"
    }) => {
      const { sourceNodeId, nodeType, level, sourceNodeType, branch = "Pass" } = params
      const sourceNode = nodes.find((n) => n.id === sourceNodeId)

      if (!sourceNode) {
        throw new Error(`${sourceNodeType ?? "Source"} node ${sourceNodeId} not found`)
      }

      const { newNode, newEdge } = createNode(level, nodeType, sourceNodeId, branch)
      const newNodeId = newNode.id

      const updatedContent = [...nodes.filter(isContentNode), newNode]
      const updatedEdges = [...edges, newEdge]

      const laidContent = layoutNodes(updatedContent, updatedEdges)

      const createdNode = laidContent.find((node) => node.id === newNodeId)
      if (!createdNode) throw new Error("Created node not found")

      setNodes(withBoxes(laidContent))
      setEdges(updatedEdges)

      setTimeout(() => {
        centerNode(createdNode)
        setFocusedNode(createdNode)
      }, 100)

      return createdNode
    },
    [nodes, edges, createNode, centerNode, setNodes, setEdges, setFocusedNode, withBoxes],
  )

  const onAddEventNode = useCallback(
    (workflowId: string) => {
      const newNodeId = crypto.randomUUID()
      const numberOfNodes = form.getValues("nodes")?.length ?? 0

      form.setValue(
        `nodes.${numberOfNodes}`,
        {
          id: newNodeId,
          parentId: null,
          type: "Event",
          workflowId,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          event: undefined,
        },
        { shouldDirty: true },
      )

      const newNode: Node = {
        id: newNodeId,
        type: "event",
        data: { workflowId },
        draggable: false,
        // Seed position is irrelevant — layoutNodes places this event by insertion order. Being
        // appended last keeps it at the right edge of its workflow.
        position: { x: 0, y: 0 },
      }

      const laidContent = layoutNodes([...nodes.filter(isContentNode), newNode], edges)
      setNodes(withBoxes(laidContent))

      const createdNode = laidContent.find((node) => node.id === newNodeId)
      if (createdNode) {
        setTimeout(() => {
          centerNode(createdNode)
          setFocusedNode(createdNode)
        }, 100)
      }
    },
    [nodes, edges, form, centerNode, setNodes, setFocusedNode, withBoxes],
  )

  // Adds a new workflow group with its first event so it shows up as a box to configure.
  const onAddWorkflow = useCallback(() => {
    const workflowId = crypto.randomUUID()
    const workflowCount = form.getValues("workflows")?.length ?? 0
    form.setValue(
      `workflows.${workflowCount}`,
      { id: workflowId, name: null, isEnabled: true },
      { shouldDirty: true },
    )
    onAddEventNode(workflowId)
  }, [form, onAddEventNode])

  // Loads an imported preview onto the canvas as a new unsaved workflow. Node ids are regenerated so
  // re-importing the same file never collides, and parent links are remapped. Nothing is persisted —
  // the user reviews/fixes (stripped references render as empty fields) and saves like any edit.
  const onImportWorkflow = useCallback(
    (preview: ImportPreview) => {
      const workflowId = crypto.randomUUID()
      const idMap = new Map(preview.nodes.map((node) => [node.id, crypto.randomUUID()]))

      const importedNodes = preview.nodes.map((node) => ({
        ...node,
        id: idMap.get(node.id)!,
        parentId: node.parentId ? (idMap.get(node.parentId) ?? null) : null,
        workflowId,
      }))

      const existingWorkflows = form.getValues("workflows") ?? []
      const existingNodes = form.getValues("nodes") ?? []

      form.setValue(
        "workflows",
        [...existingWorkflows, { id: workflowId, name: preview.name, isEnabled: true }],
        { shouldDirty: true },
      )
      form.setValue("nodes", [...existingNodes, ...importedNodes], { shouldDirty: true })

      const { nodes: laidContent, edges: rebuiltEdges } = getInitialNodesAndEdges({ form })
      setNodes(withBoxes(laidContent))
      setEdges(rebuiltEdges)
      form.trigger()
    },
    [form, withBoxes, setNodes, setEdges],
  )

  const onToggleWorkflowEnabled = useCallback(
    (workflowId: string) => {
      const index = (form.getValues("workflows") ?? []).findIndex((w) => w.id === workflowId)
      if (index < 0) return
      const current = form.getValues(`workflows.${index}.isEnabled`)
      form.setValue(`workflows.${index}.isEnabled`, !current, { shouldDirty: true })
      // Recompute so the disabled workflow's cards shift down (or back up) for its banner row.
      setNodes(withBoxes(layoutNodes(nodes.filter(isContentNode), edges)))
      form.trigger()
    },
    [nodes, edges, form, setNodes, withBoxes],
  )

  const onDeleteWorkflow = useCallback(
    (workflowId: string) => {
      const idsToDelete = new Set(
        (form.getValues("nodes") ?? []).filter((n) => n.workflowId === workflowId).map((n) => n.id),
      )

      if (focusedNode && idsToDelete.has(focusedNode.id)) setFocusedNode(null)

      const updatedContent = nodes.filter(isContentNode).filter((n) => !idsToDelete.has(n.id))
      const updatedEdges = edges.filter(
        (e) => !idsToDelete.has(e.source) && !idsToDelete.has(e.target),
      )

      // Drop the workflow from the form first so its box isn't recomputed.
      form.setValue(
        "nodes",
        (form.getValues("nodes") ?? []).filter((n) => n.workflowId !== workflowId),
        { shouldDirty: true },
      )
      form.setValue(
        "workflows",
        (form.getValues("workflows") ?? []).filter((w) => w.id !== workflowId),
        { shouldDirty: true },
      )

      setNodes(withBoxes(layoutNodes(updatedContent, updatedEdges)))
      setEdges(updatedEdges)
      form.trigger()
    },
    [nodes, edges, focusedNode, form, setFocusedNode, setNodes, setEdges, withBoxes],
  )

  const onAddConditionNode = useCallback(
    (nodeId: string, kind: "event" | "condition" | "action", branch: "Pass" | "Fail" = "Pass") => {
      const node = nodes.find((n) => n.id === nodeId)
      if (!node) throw new Error(`Node ${nodeId} not found`)
      if (!["event", "condition", "action"].includes(kind)) {
        throw new Error(`Invalid node kind ${kind} for condition node`)
      }

      const conditionLevel = node.position.y + CARD_HEIGHT + CARD_SPACING

      addNodeAndUpdateFlow({
        sourceNodeId: nodeId,
        level: conditionLevel,
        nodeType: "condition",
        sourceNodeType: kind,
        branch,
      })
    },
    [nodes, addNodeAndUpdateFlow],
  )

  const onAddActionNode = useCallback(
    (nodeId: string, kind: "event" | "condition" | "action", branch: "Pass" | "Fail" = "Pass") => {
      const node = nodes.find((n) => n.id === nodeId)
      if (!node) throw new Error(`Node ${nodeId} not found`)
      if (!["event", "condition", "action"].includes(kind)) {
        throw new Error(`Invalid node kind ${kind} for action node`)
      }

      const actionLevel = node.position.y + CARD_HEIGHT + CARD_SPACING

      addNodeAndUpdateFlow({
        sourceNodeId: nodeId,
        level: actionLevel,
        nodeType: "action",
        sourceNodeType: kind,
        branch,
      })
    },
    [nodes, addNodeAndUpdateFlow],
  )

  const onDeleteNode = useCallback(
    (nodeId: string) => {
      const nodeToDelete = nodes.find((n) => n.id === nodeId)
      if (!nodeToDelete) {
        throw new Error(`Node ${nodeId} not found`)
      }

      const getAllDescendantIds = (currentNodeId: string): string[] => {
        const directChildrenEdges = edges.filter((e) => e.source === currentNodeId)
        const directChildrenIds = directChildrenEdges.map((e) => e.target)

        let allDescendants: string[] = [...directChildrenIds]
        for (const childId of directChildrenIds) {
          allDescendants = [...allDescendants, ...getAllDescendantIds(childId)]
        }
        return allDescendants
      }

      const descendantIds = getAllDescendantIds(nodeId)
      const allNodesToDeleteIds = [nodeId, ...descendantIds]

      const updatedContent = nodes
        .filter(isContentNode)
        .filter((n) => !allNodesToDeleteIds.includes(n.id))
      const updatedEdges = edges.filter(
        (e) => !allNodesToDeleteIds.includes(e.source) && !allNodesToDeleteIds.includes(e.target),
      )

      if (focusedNode && allNodesToDeleteIds.includes(focusedNode.id)) {
        setFocusedNode(null)
      }

      // Remove the form nodes first so a workflow left empty doesn't keep its box.
      const newFormNodes = form
        .getValues("nodes")
        ?.filter((n) => !allNodesToDeleteIds.includes(n.id))
      form.setValue("nodes", newFormNodes, { shouldDirty: true, shouldTouch: true })

      // Drop any workflow whose last node was just deleted: an empty workflow renders no box, so it
      // would otherwise linger invisibly and block Save.
      const remainingWorkflowIds = new Set((newFormNodes ?? []).map((n) => n.workflowId))
      form.setValue(
        "workflows",
        (form.getValues("workflows") ?? []).filter((w) => remainingWorkflowIds.has(w.id)),
        { shouldDirty: true },
      )

      setNodes(withBoxes(layoutNodes(updatedContent, updatedEdges)))
      setEdges(updatedEdges)
      form.trigger()

      // Ensure dirtyFields is updated for the BuilderFooter check
      // @ts-expect-error - We need to manually set this to ensure the form is marked as dirty
      form.formState.dirtyFields.nodes = true
    },
    [nodes, edges, focusedNode, setFocusedNode, setNodes, setEdges, form, withBoxes],
  )

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds) as Node[])
  }, [])

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    setEdges((eds) => applyEdgeChanges(changes, eds) as BuiltInEdge[])
  }, [])

  return (
    <Form
      {...form}
      errorTranslations={getErrorTranslations()}
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault()
          e.stopPropagation()
        }
      }}
      className="h-full"
    >
      <BuilderSaveContext.Provider value={saveCtx}>
        <BuilderContext.Provider
          value={{
            container,
            form,
            nodes,
            edges,
            setNodes: onNodesChange,
            setEdges: onEdgesChange,
            focusedNode,
            setFocusedNode: setFocusedNodeId,
            onAddWorkflow,
            onImportWorkflow,
            onAddEventNode,
            onToggleWorkflowEnabled,
            onDeleteWorkflow,
            onAddConditionNode,
            onAddActionNode,
            onDeleteNode,
          }}
        >
          {children}
        </BuilderContext.Provider>
      </BuilderSaveContext.Provider>
    </Form>
  )
}
